/**
 * Navigation Search Component
 * - Quick navigation search with keyboard shortcuts
 * - Compact icon-only trigger when sidebar is collapsed
 * - Elegant modal with smooth animations
 * - Keyboard: Ctrl/Cmd+K to open, ESC to close, arrows + Enter to navigate
 */

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAppSelector } from "../../store";
import { useNavigationSearch } from "../../hooks/useNavigation";
import {
  HomeIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  UsersIcon,
  CalendarDaysIcon,
  WrenchScrewdriverIcon,
  CreditCardIcon,
  ChartBarIcon,
  BuildingLibraryIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  BellIcon,
  ClipboardDocumentListIcon,
  ArchiveBoxIcon,
  BanknotesIcon,
  BeakerIcon,
  BookOpenIcon,
  MapIcon,
  PhotoIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  HomeModernIcon,
  DocumentTextIcon,
  UserPlusIcon,
  MegaphoneIcon,
} from "@heroicons/react/24/outline";
import { GaugeIcon, ChartArea } from "lucide-react"; // For meter readings

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

// Quick actions for common tasks
interface QuickAction {
  id: string;
  name: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  type: 'action';
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'add_property',
    name: 'Add Property',
    description: 'Create a new property',
    href: '/properties?action=addProperty',
    icon: <BuildingOfficeIcon className="w-5 h-5" />,
    type: 'action',
  },
  {
    id: 'add_rental_unit',
    name: 'Add Rental Unit',
    description: 'Create a new rental unit',
    href: '/rental-units?action=addUnit',
    icon: <HomeModernIcon className="w-5 h-5" />,
    type: 'action',
  },
  {
    id: 'add_tenant',
    name: 'Add Tenant',
    description: 'Onboard a new tenant',
    href: '/tenants?action=addTenant',
    icon: <UsersIcon className="w-5 h-5" />,
    type: 'action',
  },
  {
    id: 'add_meter_readings',
    name: 'Add Meter Readings',
    description: 'Log the latest utility usage',
    href: '/meter-readings',
    icon: <GaugeIcon className="w-5 h-5" />,
    type: 'action',
  },
  {
    id: 'add_maintenance_request',
    name: 'Add Maintenance Request',
    description: 'Log a new maintenance issue',
    href: '/maintenance/create',
    icon: <WrenchScrewdriverIcon className="w-5 h-5" />,
    type: 'action',
  },
  {
    id: 'create_invoice',
    name: 'Create Invoice',
    description: 'Generate a new invoice',
    href: '/invoices/create',
    icon: <DocumentTextIcon className="w-5 h-5" />,
    type: 'action',
  },
  {
    id: 'analyze_profit_loss',
    name: 'Analyze P/L',
    description: 'Review profit and loss analytics',
    href: '/analytics?tab=profit_loss',
    icon: <ChartArea className="w-5 h-5" />,
    type: 'action',
  },
  {
    id: 'analyze_collections',
    name: 'Analyze Collections',
    description: 'Review collections performance and recovery',
    href: '/analytics?tab=collections',
    icon: <BanknotesIcon className="w-5 h-5" />,
    type: 'action',
  },
  {
    id: 'analyze_revenue',
    name: 'Analyze Revenue',
    description: 'Inspect revenue trends and billed amounts',
    href: '/analytics?tab=revenue',
    icon: <ChartBarIcon className="w-5 h-5" />,
    type: 'action',
  },
  {
    id: 'analyze_maintenance',
    name: 'Analyze Maintenance',
    description: 'Track maintenance workload and outcomes',
    href: '/analytics?tab=maintenance',
    icon: <WrenchScrewdriverIcon className="w-5 h-5" />,
    type: 'action',
  },
  {
    id: 'add_expense',
    name: 'Add Expense',
    description: 'Log a new expense',
    href: '/expenses/list?action=addExpense',
    icon: <BanknotesIcon className="w-5 h-5" />,
    type: 'action',
  },
  {
    id: 'add_expense_payee',
    name: 'Add Expense Payee',
    description: 'Create a payee account',
    href: '/expenses/accounts?action=addExpensePayee',
    icon: <UserPlusIcon className="w-5 h-5" />,
    type: 'action',
  },
  {
    id: 'invite_company_user',
    name: 'Invite Company User',
    description: 'Send an invite to a teammate',
    href: '/company-users?action=inviteUser',
    icon: <UserGroupIcon className="w-5 h-5" />,
    type: 'action',
  },
  {
    id: 'create_broadcast',
    name: 'Create Broadcast',
    description: 'Send a new announcement',
    href: '/broadcasts/create',
    icon: <MegaphoneIcon className="w-5 h-5" />,
    type: 'action',
  },
];

interface NavigationSearchProps {
  onItemClick?: () => void;
  compact?: boolean; // icon-only trigger when sidebar is collapsed
  showText?: boolean; // delay showing label/shortcut until fully expanded
  // When this number changes, the modal opens (controlled by parent hotkey)
  openSignal?: number;
}

const NavigationSearch: React.FC<NavigationSearchProps> = ({
  onItemClick,
  compact = false,
  showText = true,
  openSignal,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const navigate = useNavigate();

  const { propertiesList } = useAppSelector((state) => state.properties);
  const { user, currentCompanyUser } = useAppSelector((state) => state.auth);

  const { filteredNavigation } = useNavigationSearch(searchTerm);

  const hasPropertyAccess = React.useMemo(() => {
    if (user?.role === "super_admin") return true;
    return (currentCompanyUser?.featurePermissions || []).includes("properties_read");
  }, [user?.role, currentCompanyUser?.featurePermissions]);

  const filteredProperties = React.useMemo(() => {
    if (!hasPropertyAccess) return [];

    const term = searchTerm.trim().toLowerCase();
    if (!term) return [];

    return propertiesList
      .map((property) => {
        const name = (property.name || property.title || "").trim();
        const lowerName = name.toLowerCase();
        if (!lowerName.includes(term)) return null;

        const score = lowerName === term ? 0 : lowerName.startsWith(term) ? 1 : 2;
        return {
          id: `property_${property._id}`,
          name,
          description: "Open property details",
          href: `/properties?id=${property._id}`,
          type: "property" as const,
          score,
        };
      })
      .filter((item): item is NonNullable<typeof item> => Boolean(item))
      .sort((a, b) => {
        if (a.score !== b.score) return a.score - b.score;
        return a.name.localeCompare(b.name);
      })
      .slice(0, 12);
  }, [hasPropertyAccess, searchTerm, propertiesList]);

  // Filter quick actions based on search term
  const filteredActions = React.useMemo(() => {
    if (!searchTerm.trim()) return QUICK_ACTIONS;
    const lowercaseSearch = searchTerm.toLowerCase();
    return QUICK_ACTIONS.filter(action =>
      action.name.toLowerCase().includes(lowercaseSearch) ||
      action.description.toLowerCase().includes(lowercaseSearch)
    );
  }, [searchTerm]);

  // Combined items for keyboard navigation (navigation, properties, then actions)
  const allItems = React.useMemo(() => {
    return [
      ...filteredNavigation.map(n => ({ ...n, isAction: false as const })),
      ...filteredProperties.map(p => ({ ...p, isAction: false as const })),
      ...filteredActions.map(a => ({ ...a, isAction: true as const })),
    ];
  }, [filteredActions, filteredNavigation, filteredProperties]);

  const hasAnyResults = allItems.length > 0;

  // External open signal from parent (open when the numeric signal changes)
  const lastSignalRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (openSignal !== undefined && openSignal !== lastSignalRef.current) {
      lastSignalRef.current = openSignal;
      setIsOpen(true);
      const t = window.setTimeout(() => inputRef.current?.focus(), 80);
      return () => window.clearTimeout(t);
    }
  }, [openSignal]);

  // In-modal navigation keys (only when open)
  useEffect(() => {
    if (!isOpen) return;
    const onNavKeys = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      } else if (e.key === "ArrowDown" && allItems.length > 0) {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, allItems.length - 1));
      } else if (e.key === "ArrowUp" && allItems.length > 0) {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && allItems[selectedIndex]) {
        e.preventDefault();
        const item = allItems[selectedIndex];
        navigate(`/dashboard${item.href}`);
        handleClose();
      }
    };
    window.addEventListener("keydown", onNavKeys);
    return () => window.removeEventListener("keydown", onNavKeys);
  }, [isOpen, allItems, selectedIndex, navigate]);

  // Reset selection on term change
  useEffect(() => setSelectedIndex(0), [searchTerm]);

  // Auto-scroll selected item into view
  useEffect(() => {
    if (isOpen && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedIndex, isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setSearchTerm("");
    setSelectedIndex(0);
    onItemClick?.();
  };

  const handleItemClick = (href: string) => {
    navigate(`/dashboard${href}`);
    handleClose();
  };

  // Use same icon set as sidebar by id
  const renderIcon = (id: string) => {
    switch (id) {
      case "dashboard":
        return <HomeIcon className="w-5 h-5" />;
      case "profile":
        return <UserCircleIcon className="w-5 h-5" />;
      case "properties":
        return <BuildingOfficeIcon className="w-5 h-5" />;
      case "rental_units":
        return <HomeModernIcon className="w-5 h-5" />;
      case "tenants":
        return <UsersIcon className="w-5 h-5" />;
      case "bookings":
        return <CalendarDaysIcon className="w-5 h-5" />;
      case "maintenance":
        return <WrenchScrewdriverIcon className="w-5 h-5" />;
      case "inventory":
        return <ArchiveBoxIcon className="w-5 h-5" />;
      case "payments":
        return <CreditCardIcon className="w-5 h-5" />;
      case "expenses":
        return <BanknotesIcon className="w-5 h-5" />;
      case "analytics":
        return <ChartBarIcon className="w-5 h-5" />;
      case "companies":
        return <BuildingLibraryIcon className="w-5 h-5" />;
      case "company_users":
        return <UserGroupIcon className="w-5 h-5" />;
      case "admin":
        return <Cog6ToothIcon className="w-5 h-5" />;
      case "notifications":
        return <BellIcon className="w-5 h-5" />;
      case "notification_logs":
        return <ClipboardDocumentListIcon className="w-5 h-5" />;
      case "test_notifications":
        return <BeakerIcon className="w-5 h-5" />;
      case "map_demo":
        return <MapIcon className="w-5 h-5" />;
      case "virtual_tours":
        return <PhotoIcon className="w-5 h-5" />;
      case "chat":
        return <ChatBubbleLeftRightIcon className="w-5 h-5" />;
      case "invoices":
        return <DocumentTextIcon className="w-5 h-5" />;
      case "meter_readings":
        return <GaugeIcon className="w-5 h-5" />;
      case "company_catalogue":
        return <BookOpenIcon className="w-5 h-5" />;
      case "broadcasts":
        return <MegaphoneIcon className="w-5 h-5" />;
      default:
        return <span className="inline-block w-5 h-5 text-center">•</span>;
    }
  };

  return (
    <>
      {/* Trigger */}
      <motion.button
        onClick={() => {
          setIsOpen(true);
        }}
        whileHover={{ scale: compact ? 1.05 : 1.01 }}
        whileTap={{ scale: 0.98 }}
        className={`group focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 ${
          compact
            ? "w-10 h-10 p-0 flex items-center justify-center rounded-lg bg-gray-50 hover:bg-primary-50 text-gray-600 hover:text-primary-600 border border-gray-200 hover:border-primary-200"
            : "w-full px-3.5 py-2.5 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100/50 hover:from-primary-50 hover:to-primary-100/50 text-gray-700 hover:text-primary-700 shadow-sm border border-gray-200 hover:border-primary-200 flex items-center gap-2"
        } text-sm transition-all duration-100 ease-out`}
        aria-label="Quick navigation search"
        title={`Quick Search (${isMac ? '⌘' : 'Ctrl+'}K)`}
      >
        <MagnifyingGlassIcon
          className={`${
            compact ? "w-5 h-5" : "w-4 h-4"
          } text-gray-500 group-hover:text-primary-600 transition-colors`}
        />
        {!compact && (
          <>
            <span
              className={`flex-1 text-left text-sm font-medium transition-opacity duration-200 ${
                showText ? "opacity-100" : "opacity-0"
              }`}
            >
              Quick Search
            </span>
            <kbd
              className={`hidden sm:inline-flex items-center gap-0.5 px-1 py-0.5 text-[10px] font-medium text-gray-500 group-hover:text-primary-500 bg-gray-50 border border-gray-200 group-hover:border-primary-200 rounded transition-all duration-200 ${
                showText ? "opacity-100" : "opacity-0"
              }`}
            >
              <span className={`${isMac ? 'text-[14px]' : 'text-[10px]'}`}>{isMac ? '⌘' : 'Ctrl+'}</span>K
            </kbd>
          </>
        )}
        {compact && <span className="sr-only">Quick Search</span>}
      </motion.button>

      {/* Modal */}
      {isOpen && (
        <>
          {createPortal(
            <div className="fixed inset-0 z-[9999] overflow-y-auto">
              <div className="flex items-center justify-center min-h-screen px-4 py-10">
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm"
                  onClick={handleClose}
                />

                {/* Dialog */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  role="dialog"
                  aria-modal="true"
                  className="relative w-full max-w-2xl max-h-[80vh] overflow-hidden rounded-2xl bg-white border border-gray-200 shadow-2xl ring-1 ring-gray-900/5 z-10"
                >
                  {/* Header */}
                  <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-primary-50 to-purple-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg shadow-primary-500/20">
                        <MagnifyingGlassIcon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-lg font-semibold text-gray-900">Navigate & take actions</h2>
                        <p className="text-xs text-gray-500">Navigate or act across platform instantly</p>
                      </div>
                      <button
                        onClick={handleClose}
                        className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white/80 transition-colors focus:outline-none"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Search Input */}
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus-within:border-primary-300 focus-within:bg-white focus-within:ring-2 focus-within:ring-primary-100 transition-all">
                      <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
                      <input
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Type to search navigation items..."
                        autoFocus
                        className="flex-1 bg-transparent !p-0 outline-none focus:outline-none ring-0 focus:ring-0 text-base text-gray-900 placeholder-gray-400 border-none"
                        autoComplete="off"
                      />
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                {/* Results */}
                <div className="max-h-[60vh] overflow-y-auto bg-white/20">
                  {hasAnyResults ? (
                    <>
                      <div className="px-5 py-2 text-xs font-medium text-gray-500 bg-white/40 border-b border-white/50">
                        {allItems.length} result{allItems.length !== 1 ? "s" : ""}
                      </div>
                      <div className="py-2">
                        {/* Navigation Section */}
                        {filteredNavigation.length > 0 && (
                          <>
                            <div className="px-5 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                              Navigation
                            </div>
                            {filteredNavigation.map((item, navIndex) => (
                              <button
                                key={item.id}
                                ref={(el) => { itemRefs.current[navIndex] = el; }}
                                onClick={() => handleItemClick(item.href)}
                                className={`relative w-full flex items-center gap-3 px-5 py-3 text-left transition-all ${
                                  navIndex === selectedIndex
                                    ? "bg-primary-50/70 text-primary-900 ring-1 ring-primary-200/50 shadow-sm"
                                    : "hover:bg-white/60 text-gray-800"
                                }`}
                              >
                                {/* Accent bar */}
                                <span
                                  className={`absolute left-0 top-0 h-full w-1 rounded-r ${
                                    navIndex === selectedIndex
                                      ? "bg-gradient-to-b from-primary-500 to-primary-400"
                                      : "bg-transparent"
                                  }`}
                                  aria-hidden="true"
                                />

                                {/* Icon */}
                                <span className="w-9 h-9 rounded-lg bg-white/70 ring-1 ring-white/50 flex items-center justify-center text-gray-700 shadow-sm">
                                  {renderIcon(item.id)}
                                </span>

                                {/* Text */}
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold truncate">
                                    {item.name}
                                  </div>
                                  {item.description && (
                                    <div className="text-sm text-gray-600 truncate mt-0.5">
                                      {item.description}
                                    </div>
                                  )}
                                </div>

                                {/* Chevron */}
                                {navIndex === selectedIndex && (
                                  <svg
                                    className="w-5 h-5 text-primary-600 ml-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 5l7 7-7 7"
                                    />
                                  </svg>
                                )}
                              </button>
                            ))}
                          </>
                        )}

                        {/* Properties Section */}
                        {filteredProperties.length > 0 && (
                          <>
                            <div className="px-5 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider mt-2">
                              Properties
                            </div>
                            {filteredProperties.map((property, propertyIndex) => {
                              const globalIndex = filteredNavigation.length + propertyIndex;
                              return (
                                <button
                                  key={property.id}
                                  ref={(el) => { itemRefs.current[globalIndex] = el; }}
                                  onClick={() => handleItemClick(property.href)}
                                  className={`relative w-full flex items-center gap-3 px-5 py-3 text-left transition-all ${
                                    globalIndex === selectedIndex
                                      ? "bg-blue-50/70 text-blue-900 ring-1 ring-blue-200/50 shadow-sm"
                                      : "hover:bg-white/60 text-gray-800"
                                  }`}
                                >
                                  <span
                                    className={`absolute left-0 top-0 h-full w-1 rounded-r ${
                                      globalIndex === selectedIndex
                                        ? "bg-gradient-to-b from-blue-500 to-blue-400"
                                        : "bg-transparent"
                                    }`}
                                    aria-hidden="true"
                                  />

                                  <span className="w-9 h-9 rounded-lg bg-blue-100 ring-1 ring-blue-200/50 flex items-center justify-center text-blue-600 shadow-sm">
                                    <BuildingOfficeIcon className="w-5 h-5" />
                                  </span>

                                  <div className="flex-1 min-w-0">
                                    <div className="font-semibold truncate">
                                      {property.name}
                                    </div>
                                    <div className="text-sm text-gray-600 truncate mt-0.5">
                                      {property.description}
                                    </div>
                                  </div>

                                  <span className="px-2 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded-full">
                                    Property
                                  </span>

                                  {globalIndex === selectedIndex && (
                                    <svg
                                      className="w-5 h-5 text-blue-600 ml-1"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                      />
                                    </svg>
                                  )}
                                </button>
                              );
                            })}
                          </>
                        )}

                        {/* Quick Actions Section */}
                        {filteredActions.length > 0 && (
                          <>
                            <div className="px-5 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider mt-2">
                              Quick Actions
                            </div>
                            {filteredActions.map((action, actionIndex) => {
                              const globalIndex = filteredNavigation.length + filteredProperties.length + actionIndex;
                              return (
                                <button
                                  key={action.id}
                                  ref={(el) => { itemRefs.current[globalIndex] = el; }}
                                  onClick={() => handleItemClick(action.href)}
                                  className={`relative w-full flex items-center gap-3 px-5 py-3 text-left transition-all ${
                                    globalIndex === selectedIndex
                                      ? "bg-green-50/70 text-green-900 ring-1 ring-green-200/50 shadow-sm"
                                      : "hover:bg-white/60 text-gray-800"
                                  }`}
                                >
                                  {/* Accent bar */}
                                  <span
                                    className={`absolute left-0 top-0 h-full w-1 rounded-r ${
                                      globalIndex === selectedIndex
                                        ? "bg-gradient-to-b from-green-500 to-green-400"
                                        : "bg-transparent"
                                    }`}
                                    aria-hidden="true"
                                  />

                                  {/* Icon */}
                                  <span className="w-9 h-9 rounded-lg bg-green-100 ring-1 ring-green-200/50 flex items-center justify-center text-green-600 shadow-sm">
                                    {action.icon}
                                  </span>

                                  {/* Text */}
                                  <div className="flex-1 min-w-0">
                                    <div className="font-semibold truncate">
                                      {action.name}
                                    </div>
                                    <div className="text-sm text-gray-600 truncate mt-0.5">
                                      {action.description}
                                    </div>
                                  </div>

                                  {/* Action badge */}
                                  <span className="px-2 py-0.5 text-[10px] font-medium bg-green-100 text-green-700 rounded-full">
                                    Action
                                  </span>

                                  {/* Chevron */}
                                  {globalIndex === selectedIndex && (
                                    <svg
                                      className="w-5 h-5 text-green-600 ml-1"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                      />
                                    </svg>
                                  )}
                                </button>
                              );
                            })}
                          </>
                        )}
                      </div>
                    </>
                  ) : searchTerm ? (
                    <div className="px-6 py-14 text-center text-gray-600">
                      <div className="text-6xl mb-4">🔍</div>
                      <div className="font-semibold text-lg mb-2">
                        No results found
                      </div>
                      <div className="text-gray-500">
                        Try a different search term
                      </div>
                    </div>
                  ) : (
                    <div className="px-6 py-14 text-center text-gray-600">
                      <div className="text-6xl mb-4">⌨️</div>
                      <div className="font-semibold text-lg mb-2">
                        Quick Navigation
                      </div>
                      <div className="text-gray-500 mb-6">
                        Type to search navigation items
                      </div>
                      <div className="inline-flex items-center space-x-6 text-sm bg-white/70 backdrop-blur-md px-4 py-2 rounded-lg ring-1 ring-white/50">
                        <div className="flex items-center space-x-1">
                          <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">
                            ↑↓
                          </kbd>
                          <span>navigate</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">
                            ↵
                          </kbd>
                          <span>select</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">
                            ESC
                          </kbd>
                          <span>close</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>,
          document.body
        )}
        </>
      )}
    </>
  );
};

export default NavigationSearch;
